<script setup lang="ts">
import { inject, ref } from 'vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { useForm } from 'vee-validate';
import { getLabel } from '@/utils/data-display-utils';
import { VueCookies } from 'vue-cookies';
import { useTracker } from '@/composables/useTracker';
import { applyConsentUpdate, ensureGtag } from '@/utils/gtag';
import { Locales } from '@/store/language.interface';

const props = defineProps<{
  languageEnabled?: Locales[];
}>();

const cookies = inject<VueCookies>('$cookies');

// REFS
const cookieConsentDialog = ref<InstanceType<typeof BaseDialog>>();

const { handleSubmit, meta } = useForm();
const { track } = useTracker();

// FUNCTIONS
function openDialog() {
  track({ event: 'cookie_consent_view' });
  cookieConsentDialog.value?.openDialog();
}

function closeDialog() {
  cookieConsentDialog.value?.closeDialog();
}

const accept = handleSubmit(() => {
  ensureGtag();
  applyConsentUpdate(true);
  cookies.set('consent', 1);
  closeDialog();
});

const refuse = handleSubmit(() => {
  ensureGtag();
  applyConsentUpdate(false);
  cookies.set('consent', 0);
  closeDialog();
});

defineExpose({
  openDialog,
  closeDialog,
});
</script>

<template>
  <BaseDialog
    ref="cookieConsentDialog"
    :is-dirty="true"
    :position="'bottom'"
    :have-close-button="false"
    class="cookie-consent"
  >
    <template v-slot:title>
      <div class="cookie-consent__title">
        {{ getLabel('dialog.cookieConsent.title', props.languageEnabled) }}
      </div>
    </template>
    <template v-slot:content>
      <div>
        {{ getLabel('dialog.cookieConsent.description', props.languageEnabled) }}
      </div>
      <div class="cookie-consent__actions">
        <BaseButton is-transparent class="primary-reverse" @click="refuse">{{
          getLabel('dialog.cookieConsent.accept-only', props.languageEnabled)
        }}</BaseButton>
        <BaseButton color="primary" @click="accept">{{
          getLabel('dialog.cookieConsent.accept-all', props.languageEnabled)
        }}</BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.cookie-consent {
  .q-card {
    border-radius: 10px;
    padding: $space-sm;

    &__section {
      align-items: start !important;
    }
  }

  .q-btn {
    font-weight: bold;
    width: 190px;
  }

  .base-dialog__content {
    display: flex;
    gap: $space-md;
  }

  &__title {
    font-size: $body-text;
    font-weight: bold;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    justify-content: end;
    width: 100%;
    gap: $space-sm;
  }

  .primary-reverse {
    border: 1px solid $color-primary-darken;
    color: $color-primary-darken;
  }
}
@media screen and (max-width: $breakpoint-sm) {
  .cookie-consent {
    .q-btn {
      font-size: 12px;
      width: 160px;
    }
  }
}
</style>
